/**
 * English translations – the reference language.
 *
 * This is the single file holding every English text in the application, one
 * top-level key per i18next namespace. Add new texts here first: the other
 * languages are typed against this object, so a key added here is immediately
 * available (and type checked) everywhere else.
 *
 * See `src/translations/index.ts` for how to add another language.
 */

const en = {
    common: {
        language: {
            select: 'Select language'
        },
        menu: {
            collapse: 'Collapse menu',
            compounds: 'Compounds',
            targets: 'Targets',
            alterations: 'Alterations detected'
        },
        footer: {
            ifu: 'Instructions for use',
            productLabel: 'Product label',
            disclaimer: 'Disclaimer',
            calculationDate: 'Calculation date: {{date}}'
        },
        metaInfo: {
            tumorset: 'Tumorset',
            fileName: 'File name',
            tumorsetMissing: 'Tumorset data is missing in this file.'
        },
        a11y: {
            appLogo: 'App Logo'
        },
        labels: {
            na: 'N/A',
            reloadPage: 'Reload page',
            unnamedCompound: 'Unnamed compound',
            unnamedDriver: 'Unnamed driver',
            unnamedTarget: 'Unnamed target'
        },
        tableRowLabels: {
            DRIVER: 'Driver',
            BIOMARKER: 'Biomarker',
            NON_CONFIRMED_DRIVER: 'Conflicting driver',
            VUS: 'Variant of unknown significance',
            NON_DRIVER: 'Non-driver',
            VUS_IN_DRIVER: 'VUS in driver',
            GENOMIC_MARKER: 'Genomic marker'
        },
        testTypeAndTestResult: {
            NGS_NORMAL: 'wild type gene',
            NGS_MUTANT: 'mutant gene',
            NGS_UNKNOWN: 'unknown status gene',
            AMPLIFICATION_MUTANT: 'amplification presence',
            AMPLIFICATION_NORMAL: 'amplification absence',
            TRANSLOCATION_MUTANT: 'translocation presence',
            TRANSLOCATION_NORMAL: 'translocation absence',
            LOSS_MUTANT: 'loss presence',
            LOSS_NORMAL: 'loss absence',
            IHC_NORMAL: 'normal',
            IHC_OVER: 'protein overexpression',
            IHC_BELOW: 'protein lack of expression',
            MSI_HIGH: 'MSI-H',
            MSI_LOW: 'MSI-L',
            MSI_MSS: 'MSS',
            TMB_HIGH: 'TMB-H',
            TMB_LOW: 'TMB-L',
            HRD_POSITIVE: 'HRD-positive',
            HRD_NEGATIVE: 'HRD-negative',
            TIL_HIGH: 'TIL-H',
            TIL_LOW: 'TIL-L',
            IHC_LOW: 'low expression',
            UNKNOWN: ''
        },
        buttons: {
            goBackHome: 'Go Back Home'
        },
        errors: {
            404: '404 – Not Found',
            notFound: {
                description: 'Page not found.'
            },
            unknownError: 'Unknown error',
            unexpected: 'Some unexpected error happened.'
        },
        contact: {
            mailTo: '<1>support@genomate.health</1>',
            haveQuestions: 'If you have any questions',
            contactUsAnyTime: 'please feel free to contact us any time at'
        },
        fileUploader: {
            disabledMessage: 'Upload disabled',
            dropHereMessage: 'Drop Here',
            defaultDescription: 'Drop files or click to browse',
            fileTypeErrorMessage: 'File type is not supported',
            fileSizeErrorMessage: 'File size is too big',
            fileSizeTooSmallErrorMessage: 'File size is too small',
            invalidJson: 'Invalid JSON file',
            readError: 'Error reading file',
            size: 'size',
            types: 'types'
        }
    },
    compound: {
        title: 'Compounds',
        tableHeader: {
            compound: 'Compound',
            drivers: 'Drivers',
            targets: 'Targets',
            gds: 'GDS'
        },
        noCompoundsFound: 'No compounds were found for this patient.'
    },
    molecularBiomarkers: {
        listTitle: 'Alterations detected'
    },
    targets: {
        listTitle: 'Targets',
        tableHeader: {
            target: 'Target',
            relatedDrivers: 'Related drivers',
            gts: 'GTS'
        },
        noDruggableTargetsFound:
            'No druggable targets were found for this patient.'
    },
    pages: {
        disclaimer: {
            title: 'Disclaimer'
        },
        ifu: {
            title: 'Instructions for use'
        },
        productLabel: {
            title: 'Product label'
        },
        upload: {
            title: 'Upload JSON'
        }
    },
    evidence: {
        COMPOUND: {
            type: 'compound'
        },
        TARGET: {
            type: 'target'
        },
        DRIVER: {
            type: 'alteration'
        },
        evidenceList: 'Evidence list',
        showAllRelations: 'Show all relations',
        noEvidenceAvailable: 'No Evidence Available',
        labels: {
            relationType: 'relation type',
            researchType: 'Research Type',
            tumor: 'tumor',
            histology: 'histology',
            driver: 'driver',
            biomarker: 'biomarker',
            target: 'target',
            molecularBackground: 'molecular background',
            compound: 'compound',
            score: 'score'
        }
    },
    filter: {
        filter: {
            CLINICAL: 'Clinical evidence',
            PRECLINICAL: 'Preclinical evidence',
            IN_SILICO: 'In-silico evidence',
            MATCH_TUMOR: "Patient's tumor type",
            MATCH_PROFILE: "Patient's exact alterations",
            addFilter: 'Add Filter',
            addFilterEvidence: 'Filter',
            THIS_TUMOR_TYPE: 'This tumor type',
            OTHER_TUMOR_TYPE: 'Other tumor types',
            DEVELOPMENT: 'Development',
            clearAll: 'Clear all',
            inputPlaceholder: 'Search for a Compound by name',
            or: 'OR',
            negative: 'Negative GDS',
            positive: 'Positive GDS'
        },
        filterTooltip: {
            CLINICAL:
                'Show evidence from clinical studies and trials involving human subjects',
            PRECLINICAL:
                'Show evidence from laboratory studies and animal models',
            IN_SILICO:
                'Show evidence from computational models or bioinformatic predictions',
            MATCH_TUMOR:
                'Show evidence specific to the patient’s diagnosed tumor type',
            MATCH_PROFILE:
                'Show only evidence related to the patient’s specific molecular alterations (e.g. KRAS G12D), excluding broader gene-level evidence (e.g. KRAS-mutant).'
        }
    },
    report: {
        biomarkersAndInhibitors: {
            tableHeader: {
                variant: 'Variant',
                driverClassification: 'Classification',
                gbs: 'GBS',
                chevron: ''
            },
            noDriversAndBiomarkersFound:
                'No drivers and biomarkers were found for this patient.'
        }
    }
}

/**
 * Languages other than the reference one may be incomplete – whatever is
 * missing falls back to English. Key names are still checked at compile time,
 * so a typo or a renamed key breaks the build instead of silently rendering the
 * raw key in the UI.
 */
type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends string ? T[K] : DeepPartial<T[K]>
}

export type Translation = DeepPartial<typeof en>

export default en
